import { ObjectId } from './base';

export type GeoJsonTypes = GeoJSON['type'];

export type BBox =
  | [number, number, number, number]
  | [number, number, number, number, number, number];

export type Position = number[]; // [number, number] | [number, number, number];

export interface GeoJsonObject {
  type: GeoJsonTypes;
  bbox?: BBox | undefined;
}

export type GeoJSON =
  | IPoint
  | IMultiPoint
  | ILineString
  | IMultiLineString
  | IPolygon
  | IMultiPolygon;

export interface IPoint extends GeoJsonObject {
  type: 'Point';
  coordinates: Position;
}

export interface IMultiPoint extends GeoJsonObject {
  type: 'MultiPoint';
  coordinates: Position[];
}

export interface ILineString extends GeoJsonObject {
  type: 'LineString';
  coordinates: Position[];
}

export interface IMultiLineString extends GeoJsonObject {
  type: 'MultiLineString';
  coordinates: Position[][];
}

export interface IPolygon extends GeoJsonObject {
  type: 'Polygon';
  coordinates: Position[][];
}

export interface IMultiPolygon extends GeoJsonObject {
  type: 'MultiPolygon';
  coordinates: Position[][][];
}

export enum MapType {
  SECTOR = 'SECTOR',
  MUNICIPALITY = 'MUNICIPALITY',
}

export enum PolygonLayerType {
  BEAT = 'BEAT',
  MUNICIPALITY = 'MUNICIPALITY',
}

export enum LineLayerType {
  ROAD = 'ROAD',
  RAILWAY = 'RAILWAY',
  RIVER = 'RIVER',
}

export enum PointLayerType {
  TRAIN_STATION = 'TRAIN_STATION',
  BUS_STATION = 'BUS_STATION',
  FIRE_HYDRANT = 'FIRE_HYDRANT',
  POTHOLE = 'POTHOLE',
  ROAD_BLOCK = 'ROAD_BLOCK',
  FLOODING = 'FLOODING',
  LIMITED_VISIBILITY = 'LIMITED_VISIBILITY',
  DEAD_ANIMAL = 'DEAD_ANIMAL',
  DOWN_WIRES = 'DOWN_WIRES',
  DOWN_POLE = 'DOWN_POLE',
  DEBRIS = 'DEBRIS',
  OPEN_MANHOLE = 'OPEN_MANHOLE',
  ROAD_WORK = 'ROAD_WORK',
}

export enum PointLayerTypeLabel {
  TRAIN_STATION = 'Train Station',
  BUS_STATION = 'Bus Station',
  FIRE_HYDRANT = 'Fire Hydrant',
  POTHOLE = 'Pothole',
  ROAD_BLOCK = 'Road Block',
  FLOODING = 'Flooding',
  LIMITED_VISIBILITY = 'Limited Visibility',
  DEAD_ANIMAL = 'Dead Animal',
  DOWN_WIRES = 'Down Wires',
  DOWN_POLE = 'Down Pole',
  DEBRIS = 'Debris',
  OPEN_MANHOLE = 'Open Manhole',
  ROAD_WORK = 'Road Work',
}

export enum FireHydrantTapeColor {
  None = 'none',
  Unknown = 'unknown',
  Other = 'other',
  Private = 'private',
  Yellow = 'yellow',
  Whitel = 'white',
  Blue = 'blue',
  Green = 'green',
  Orange = 'orange',
  White = 'white',
  Black = 'black',
}

export interface IPolygonLayer extends ObjectId {
  name: string;
  type: PolygonLayerType;
  area: IPolygon;
  agency: string;
}

export interface ILineLayer extends ObjectId {
  name: string;
  type: LineLayerType;
  line: ILineString;
}

export interface IPointLayer extends ObjectId {
  name?: string;
  type: PointLayerType | string;
  status?: 'ACTIVE' | 'INACTIVE';
  point: IPoint;
  icon?: string;
  properties?: Record<string, any>;
  isImported?: boolean;
  createdBy?: string | { firstName?: string; lastName?: string };
  updatedAt?: string;
  uniqueId?: string;
}

export interface IMap extends ObjectId {
  name: string;
  type: MapType;
  area: IPolygon;
}

export interface IHydrantProperties {
  location: string;
  street: string;
  sideOfStreet: string;
  tapeColor: string;
  hydrantNumber: string;
  hydrantFID: string;
  fireDepartment: string;
  extension: string;
  owner: string;
  longitude: string;
  latitude: string;
}

export interface ILocation extends ObjectId {
  device: string;
  user: string;
  // UnitShift information will exist when track location for active unit shifts.
  unitShift?: string | null;
  // CFS information will exist if unit shift is assigned to CFS.
  cfs?: string | null;
  // address corresponding to the point
  address?: string | null;

  // https://github.com/transistorsoft/react-native-background-geolocation-android/wiki/Location-Data-Schema
  // https://transistorsoft.github.io/react-native-background-geolocation/interfaces/location.html
  uuid: string;
  timestamp: Date; // The time when arrived at the location
  age: number; // Age of the location in milliseconds
  is_moving: boolean; // The motion-state when location was recorded
  is_heartbeat?: boolean; // If this location was recorded during heartbeat-mode
  odometer: number;
  mock?: boolean;
  point: IPoint;
  accuracy: number;
  speed: number;
  speed_accuracy: number;
  heading: number;
  heading_accuracy: number;
  altitude: number;
  ellipsoidal_altitude: number;
  altitude_accuracy: number;

  measuredAt: Date; // The time when location is measured, it's calculated as timestamp + age
  readonly createdAt: Date; // The time when location is synced with our backend
}
